/**
 * Task lines in a document: finding them, and ticking them off.
 *
 * A checkbox is only worth showing if it can be clicked, and a checkbox in a
 * *query* — a list of tasks gathered from the whole library — is only safe to
 * click if the write goes back to the right line of the right note. So every
 * write states which line it means **and** what that line said; if the document
 * has moved on, the write is refused rather than applied to whatever is there
 * now. Nothing here guesses.
 */

/** `- [ ] text`, `* [x] text`, `1. [ ] text`, with any indentation. */
const TASK_RE = /^(\s*(?:[-*+]|\d+[.)])\s+\[)([ xX])(\]\s*)(.*)$/;

export interface TaskLine {
  /** 1-based, matching `data-source-line` in the preview. */
  line: number;
  checked: boolean;
  /** The task's text, without the marker. */
  text: string;
}

/** Every task in a document, in order. */
export function extractTasks(content: string): TaskLine[] {
  const tasks: TaskLine[] = [];
  content.split('\n').forEach((raw, index) => {
    const match = TASK_RE.exec(raw);
    if (!match) return;
    tasks.push({
      line: index + 1,
      checked: (match[2] ?? ' ').toLowerCase() === 'x',
      text: (match[4] ?? '').trim(),
    });
  });
  return tasks;
}

/**
 * Tick or untick one line. Returns the new content, or null when that line is
 * no longer the task the caller saw — the document changed underneath, and
 * rewriting it blindly would tick off the wrong thing.
 */
export function setTaskChecked(
  content: string,
  line: number,
  checked: boolean,
  expectedText?: string,
): string | null {
  const lines = content.split('\n');
  const raw = lines[line - 1];
  if (raw === undefined) return null;
  const match = TASK_RE.exec(raw);
  if (!match) return null;
  if (expectedText !== undefined && (match[4] ?? '').trim() !== expectedText.trim()) return null;

  lines[line - 1] = `${match[1]}${checked ? 'x' : ' '}${match[3]}${match[4] ?? ''}`;
  return lines.join('\n');
}

/** A task with the document it came from, for a list gathered across notes. */
export interface CollectedTask extends TaskLine {
  docId: string;
  docTitle: string;
  /** A date found in the task text (`2026-09-20`), for sorting by what is due. */
  due: string | null;
}

const DUE_RE = /\b(\d{4}-\d{2}-\d{2})\b/;

export function collectTasks(
  doc: { id: string; title: string; content: string },
  state: 'open' | 'done' | 'all',
): CollectedTask[] {
  return extractTasks(doc.content)
    .filter((task) => (state === 'all' ? true : state === 'done' ? task.checked : !task.checked))
    .map((task) => ({
      ...task,
      docId: doc.id,
      docTitle: doc.title,
      due: DUE_RE.exec(task.text)?.[1] ?? null,
    }));
}
